/**
 * 🤖 AI Kullanım İstatistik Sayfası (Cursor tarzı).
 *
 * Şirketin aylık AI token kotasını, kullanımını ve günlük kırılımı gösterir.
 * Veri: GET /api/AiChat/usage (gts_ai_api). Sayfa yalnızca 3 AI modundan
 * en az biri açık olan şirketlerde profil menüsünden erişilebilir.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TokenIcon from '@mui/icons-material/Token';
import BoltIcon from '@mui/icons-material/Bolt';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import aiChatService, { AiChatUsage } from '../../../services/aiChatService';

const formatNumber = (n: number): string => new Intl.NumberFormat('tr-TR').format(n);

/** 1.234.567 → "1,2M" gibi kısa gösterim (grafik eksenleri için) */
const formatCompact = (n: number): string =>
  new Intl.NumberFormat('tr-TR', { notation: 'compact', maximumFractionDigits: 1 }).format(n);

interface MonthOption {
  year: number;
  month: number;
  label: string;
}

const AiUsagePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [usage, setUsage] = useState<AiChatUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notEnabled, setNotEnabled] = useState(false);
  const [selected, setSelected] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'tr-TR';

  const monthName = useCallback(
    (year: number, month: number) =>
      new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' }),
    [locale]
  );

  // Ay seçici: mevcut aydan geriye 6 ay
  const monthOptions = useMemo<MonthOption[]>(() => {
    const options: MonthOption[] = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        label: monthName(d.getFullYear(), d.getMonth() + 1),
      });
    }
    return options;
  }, [monthName]);

  const loadUsage = useCallback(async (year: number, month: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiChatService.getAiChatUsage(year, month);
      setUsage(data);
      setNotEnabled(false);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setNotEnabled(true);
      } else {
        console.error('❌ [AiUsage] Kullanım verisi yüklenemedi:', err);
        setError(t('aiUsage.loadError', 'Kullanım verileri yüklenemedi.'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUsage(selected.year, selected.month);
  }, [selected, loadUsage]);

  const usagePct = useMemo(() => {
    if (!usage || usage.monthlyTokenLimit <= 0) return 0;
    return Math.min(100, (usage.usedTokens / usage.monthlyTokenLimit) * 100);
  }, [usage]);

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return selected.year === now.getFullYear() && selected.month === now.getMonth() + 1;
  }, [selected]);

  // Günlük grafik: ayın tüm günleri doldurulur (kullanım olmayan günler 0)
  const dailyChartData = useMemo(() => {
    if (!usage) return [];
    const daysInMonth = new Date(usage.year, usage.month, 0).getDate();
    const byDate = new Map(usage.daily.map((d) => [d.date, d]));
    const rows: Array<{ day: string; prompt: number; output: number; total: number; requests: number }> = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const key = `${usage.year}-${String(usage.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const row = byDate.get(key);
      rows.push({
        day: String(day),
        prompt: row?.promptTokens ?? 0,
        output: row?.outputTokens ?? 0,
        total: row?.totalTokens ?? 0,
        requests: row?.requestCount ?? 0,
      });
    }
    return rows;
  }, [usage]);

  const monthlyChartData = useMemo(() => {
    if (!usage) return [];
    return usage.months.map((m) => ({
      label: new Date(m.year, m.month - 1, 1).toLocaleDateString(locale, { month: 'short' }),
      total: m.totalTokens,
      requests: m.requestCount,
    }));
  }, [usage, locale]);

  const dailyTableRows = useMemo(() => {
    if (!usage) return [];
    return [...usage.daily].reverse(); // en yeni gün üstte
  }, [usage]);

  const barColor = (pct: number) => (pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary') as
    | 'error'
    | 'warning'
    | 'primary';

  const summaryCards = usage
    ? [
        {
          key: 'limit',
          label: t('aiUsage.monthlyLimit', 'Aylık Limit'),
          value: formatNumber(usage.monthlyTokenLimit),
          icon: <TokenIcon />,
          color: theme.palette.primary.main,
        },
        {
          key: 'used',
          label: t('aiUsage.used', 'Kullanılan'),
          value: formatNumber(usage.usedTokens),
          icon: <DonutLargeIcon />,
          color: usagePct >= 100 ? theme.palette.error.main : usagePct >= 80 ? theme.palette.warning.main : theme.palette.info.main,
        },
        {
          key: 'remaining',
          label: t('aiUsage.remaining', 'Kalan'),
          value: formatNumber(usage.remainingTokens),
          icon: <AutoAwesomeIcon />,
          color: theme.palette.success.main,
        },
        {
          key: 'requests',
          label: t('aiUsage.requests', 'İstek Sayısı'),
          value: formatNumber(usage.requestCount),
          icon: <BoltIcon />,
          color: theme.palette.secondary.main,
        },
      ]
    : [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Başlık + ay seçici */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <QueryStatsIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" fontWeight={700}>
              {t('aiUsage.title', 'AI Kullanımı')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('aiUsage.subtitle', 'Aylık token kotanızı ve günlük kullanım detaylarınızı görüntüleyin')}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={`${selected.year}-${selected.month}`}
            onChange={(e) => {
              const [y, m] = String(e.target.value).split('-').map(Number);
              setSelected({ year: y, month: m });
            }}
          >
            {monthOptions.map((opt) => (
              <MenuItem key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && notEnabled && (
        <Alert severity="info">{t('aiUsage.notEnabled', 'AI paketi şirketinizde aktif değil.')}</Alert>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && !notEnabled && usage && (
        <>
          {/* Kota uyarıları (yalnızca mevcut ay için anlamlı) */}
          {isCurrentMonth && usagePct >= 100 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('aiUsage.quotaExceeded', 'Aylık token kotanız doldu. Yeni AI istekleri gelecek ay başında tekrar kullanılabilir.')}
            </Alert>
          )}
          {isCurrentMonth && usagePct >= 80 && usagePct < 100 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('aiUsage.quotaWarning', 'Aylık token kotanızın %{{percent}} kadarını kullandınız.', {
                percent: usagePct.toFixed(0),
              })}
            </Alert>
          )}

          {/* Özet kartları */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {summaryCards.map((card) => (
              <Grid item xs={6} md={3} key={card.key}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Box sx={{ color: card.color, display: 'flex' }}>{card.icon}</Box>
                      <Typography variant="body2" color="text.secondary">
                        {card.label}
                      </Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={700}>
                      {card.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Kota barı */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('aiUsage.quotaTitle', 'Aylık Kota Kullanımı')} — {monthName(usage.year, usage.month)}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color={`${barColor(usagePct)}.main`}>
                {t('aiUsage.quotaPercent', '%{{percent}} kullanıldı', { percent: usagePct.toFixed(1) })}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={usagePct}
              color={barColor(usagePct)}
              sx={{ height: 12, borderRadius: 6, mb: 1.5 }}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.5, sm: 3 }}
              divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
            >
              <Typography variant="body2" color="text.secondary">
                {t('aiUsage.quotaOf', '{{used}} / {{limit}} token', {
                  used: formatNumber(usage.usedTokens),
                  limit: formatNumber(usage.monthlyTokenLimit),
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('aiUsage.promptTokens', 'Prompt Token')}: {formatNumber(usage.promptTokens)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('aiUsage.outputTokens', 'Çıkış Token')}: {formatNumber(usage.outputTokens)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('aiUsage.requests', 'İstek Sayısı')}: {formatNumber(usage.requestCount)}
              </Typography>
            </Stack>
          </Paper>

          {/* Günlük kullanım grafiği */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              {t('aiUsage.dailyChartTitle', 'Günlük Kullanım')} — {monthName(usage.year, usage.month)}
            </Typography>
            {usage.daily.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('aiUsage.noData', 'Bu ay için henüz kullanım verisi yok.')}
              </Typography>
            ) : (
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={dailyChartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} width={56} />
                    <RechartsTooltip
                      formatter={(value: any, name: any) => [formatNumber(Number(value)), name]}
                      labelFormatter={(day) => `${day} ${monthName(usage.year, usage.month)}`}
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="prompt"
                      name={t('aiUsage.promptTokens', 'Prompt Token')}
                      stackId="tokens"
                      fill={theme.palette.primary.main}
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="output"
                      name={t('aiUsage.outputTokens', 'Çıkış Token')}
                      stackId="tokens"
                      fill={theme.palette.success.main}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Grid container spacing={2}>
            {/* Günlük detay tablosu */}
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {t('aiUsage.dailyTableTitle', 'Günlük Detay')}
                </Typography>
                {dailyTableRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {t('aiUsage.noData', 'Bu ay için henüz kullanım verisi yok.')}
                  </Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: 380 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('aiUsage.date', 'Tarih')}</TableCell>
                          <TableCell align="right">{t('aiUsage.requests', 'İstek Sayısı')}</TableCell>
                          <TableCell align="right">{t('aiUsage.promptTokens', 'Prompt Token')}</TableCell>
                          <TableCell align="right">{t('aiUsage.outputTokens', 'Çıkış Token')}</TableCell>
                          <TableCell align="right">{t('aiUsage.totalTokens', 'Toplam Token')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dailyTableRows.map((row) => (
                          <TableRow key={row.date} hover>
                            <TableCell>
                              {new Date(`${row.date}T00:00:00`).toLocaleDateString(locale, {
                                day: '2-digit',
                                month: 'short',
                                weekday: 'short',
                              })}
                            </TableCell>
                            <TableCell align="right">{formatNumber(row.requestCount)}</TableCell>
                            <TableCell align="right">{formatNumber(row.promptTokens)}</TableCell>
                            <TableCell align="right">{formatNumber(row.outputTokens)}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} component="span">
                                {formatNumber(row.totalTokens)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Aylık trend */}
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {t('aiUsage.monthlyTrendTitle', 'Aylık Trend (Son 6 Ay)')}
                </Typography>
                {monthlyChartData.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {t('aiUsage.noData', 'Bu ay için henüz kullanım verisi yok.')}
                  </Typography>
                ) : (
                  <Box sx={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={monthlyChartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} width={56} />
                        <RechartsTooltip
                          formatter={(value: any) => [
                            formatNumber(Number(value)),
                            t('aiUsage.totalTokens', 'Toplam Token'),
                          ]}
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                          }}
                        />
                        <Bar dataKey="total" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default AiUsagePage;
